import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  FlatList,
  StyleSheet,
  Modal,
  ScrollView,
  Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useAppDispatch } from '../../store';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import { AIExplanationDisplay } from './AIExplanationDisplay';
import type {
  SkillGapRecommendationsProps,
  SkillGapRecommendation,
  SkillGapFeedback,
  LearningPathStep,
  ProgressIndicator,
} from '@coffeeplexity/shared';

interface SkillGapCardProps {
  recommendation: SkillGapRecommendation;
  onPress: () => void;
  onFeedback: (feedback: SkillGapFeedback) => void;
  showLearningPath: boolean;
}

const SkillGapCard: React.FC<SkillGapCardProps> = ({
  recommendation,
  onPress,
  onFeedback,
  showLearningPath,
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const getPriorityColor = (priority: 'high' | 'medium' | 'low') => {
    switch (priority) {
      case 'high':
        return '#F44336';
      case 'medium':
        return '#FF9800';
      case 'low':
        return '#4CAF50';
      default:
        return '#666';
    }
  };

  const getPriorityGradient = (
    priority: 'high' | 'medium' | 'low'
  ): [string, string] => {
    switch (priority) {
      case 'high':
        return ['#F44336', '#D32F2F'];
      case 'medium':
        return ['#FF9800', '#F57C00'];
      case 'low':
        return ['#4CAF50', '#388E3C'];
      default:
        return ['#666', '#444'];
    }
  };

  const formatTimeEstimate = (minutes: number): string => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) return `${hours}h`;
    return `${hours}h ${remainingMinutes}m`;
  };

  const handleFeedback = (
    relevant: boolean,
    priority: 'higher' | 'same' | 'lower',
    reason?: string
  ) => {
    const feedback: SkillGapFeedback = {
      relevant,
      priority,
      reason,
      timestamp: new Date().toISOString(),
    };
    onFeedback(feedback);
  };

  return (
    <>
      <TouchableOpacity
        style={styles.skillGapCard}
        onPress={onPress}
        activeOpacity={0.8}
      >
        <LinearGradient
          colors={getPriorityGradient(recommendation.priority)}
          style={styles.skillGapGradient}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
        >
          <View style={styles.skillGapHeader}>
            <View style={styles.skillGapTitleContainer}>
              <Text style={styles.skillGapTitle} numberOfLines={2}>
                {recommendation.skillName}
              </Text>
              <Text style={styles.skillGapCategory}>
                {recommendation.skillCategory}
              </Text>
            </View>

            <TouchableOpacity
              onPress={() => setShowDetails(true)}
              style={styles.detailsButton}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons
                name='information-circle-outline'
                size={20}
                color='#fff'
              />
            </TouchableOpacity>
          </View>

          <View style={styles.gapAnalysis}>
            <View style={styles.masteryProgress}>
              <Text style={styles.masteryLabel}>Current Mastery</Text>
              <View style={styles.masteryBar}>
                <View
                  style={[
                    styles.masteryFill,
                    { width: `${recommendation.currentMastery * 100}%` },
                  ]}
                />
              </View>
              <Text style={styles.masteryValue}>
                {Math.round(recommendation.currentMastery * 100)}%
              </Text>
            </View>

            <View style={styles.gapIndicator}>
              <Ionicons name='arrow-up' size={16} color='#fff' />
              <Text style={styles.gapSize}>
                {Math.round(recommendation.gapSize * 100)}% gap
              </Text>
            </View>
          </View>

          <Text style={styles.whyImportant} numberOfLines={2}>
            {recommendation.whyImportant}
          </Text>

          <View style={styles.skillGapFooter}>
            <View style={styles.timeEstimate}>
              <Ionicons name='time-outline' size={14} color='#fff' />
              <Text style={styles.timeText}>
                {formatTimeEstimate(recommendation.estimatedTimeToClose)}
              </Text>
            </View>

            <View style={styles.priorityBadge}>
              <Text style={styles.priorityText}>
                {recommendation.priority.toUpperCase()} PRIORITY
              </Text>
            </View>
          </View>

          {showLearningPath && recommendation.learningPath.length > 0 && (
            <View style={styles.learningPathPreview}>
              <Text style={styles.learningPathTitle}>Learning Path:</Text>
              <Text style={styles.learningPathSteps} numberOfLines={1}>
                {recommendation.learningPath
                  .slice(0, 3)
                  .map(step => step.title)
                  .join(' → ')}
                {recommendation.learningPath.length > 3 && ' → ...'}
              </Text>
            </View>
          )}
        </LinearGradient>
      </TouchableOpacity>

      {/* Skill Gap Details Modal */}
      <Modal
        visible={showDetails}
        transparent
        animationType='slide'
        onRequestClose={() => setShowDetails(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <ScrollView showsVerticalScrollIndicator={false}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>
                  {recommendation.skillName}
                </Text>
                <TouchableOpacity
                  onPress={() => setShowDetails(false)}
                  style={styles.modalCloseButton}
                >
                  <Ionicons name='close' size={24} color='#666' />
                </TouchableOpacity>
              </View>

              <View style={styles.skillOverview}>
                <View style={styles.skillStats}>
                  <View style={styles.skillStatItem}>
                    <Text style={styles.skillStatLabel}>Current Level</Text>
                    <Text style={styles.skillStatValue}>
                      {Math.round(recommendation.currentMastery * 100)}%
                    </Text>
                  </View>
                  <View style={styles.skillStatItem}>
                    <Text style={styles.skillStatLabel}>Target Level</Text>
                    <Text style={styles.skillStatValue}>
                      {Math.round(recommendation.targetMastery * 100)}%
                    </Text>
                  </View>
                  <View style={styles.skillStatItem}>
                    <Text style={styles.skillStatLabel}>Gap Size</Text>
                    <Text
                      style={[
                        styles.skillStatValue,
                        { color: getPriorityColor(recommendation.priority) },
                      ]}
                    >
                      {Math.round(recommendation.gapSize * 100)}%
                    </Text>
                  </View>
                </View>

                <View style={styles.detailedMasteryBar}>
                  <View style={styles.masteryBarContainer}>
                    <View
                      style={[
                        styles.currentMasteryFill,
                        { width: `${recommendation.currentMastery * 100}%` },
                      ]}
                    />
                    <View
                      style={[
                        styles.targetMasteryIndicator,
                        { left: `${recommendation.targetMastery * 100}%` },
                      ]}
                    />
                  </View>
                  <View style={styles.masteryLabels}>
                    <Text style={styles.masteryLabelText}>Current</Text>
                    <Text style={styles.masteryLabelText}>Target</Text>
                  </View>
                </View>
              </View>

              <AIExplanationDisplay
                explanation={{
                  title: 'Why this skill is important',
                  description: recommendation.whyImportant,
                  confidence: recommendation.metadata.confidence,
                  factors: [
                    `How it helps: ${recommendation.howItHelps}`,
                    `Time to close gap: ${formatTimeEstimate(recommendation.estimatedTimeToClose)}`,
                    `Priority: ${recommendation.priority}`,
                  ],
                }}
              />

              {recommendation.prerequisites.length > 0 && (
                <View style={styles.prerequisitesSection}>
                  <Text style={styles.sectionTitle}>Prerequisites</Text>
                  {recommendation.prerequisites.map((prereq, index) => (
                    <View key={index} style={styles.prerequisiteItem}>
                      <Ionicons
                        name='checkmark-circle-outline'
                        size={16}
                        color='#4CAF50'
                      />
                      <Text style={styles.prerequisiteText}>{prereq}</Text>
                    </View>
                  ))}
                </View>
              )}

              {recommendation.learningPath.length > 0 && (
                <View style={styles.learningPathSection}>
                  <Text style={styles.sectionTitle}>
                    Recommended Learning Path
                  </Text>
                  {recommendation.learningPath.map((step, index) => (
                    <LearningPathStepItem
                      key={step.id}
                      step={step}
                      index={index}
                    />
                  ))}
                </View>
              )}

              {recommendation.progressIndicators.length > 0 && (
                <View style={styles.progressSection}>
                  <Text style={styles.sectionTitle}>Progress Indicators</Text>
                  {recommendation.progressIndicators.map((indicator, index) => (
                    <ProgressIndicatorItem key={index} indicator={indicator} />
                  ))}
                </View>
              )}

              <View style={styles.feedbackSection}>
                <Text style={styles.feedbackTitle}>
                  Is this recommendation helpful?
                </Text>
                <View style={styles.feedbackButtons}>
                  <TouchableOpacity
                    style={[
                      styles.feedbackButton,
                      styles.feedbackButtonPositive,
                    ]}
                    onPress={() => {
                      handleFeedback(true, 'same', 'Relevant and actionable');
                      setShowDetails(false);
                    }}
                  >
                    <Ionicons name='thumbs-up' size={16} color='#4CAF50' />
                    <Text style={styles.feedbackButtonText}>Helpful</Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[
                      styles.feedbackButton,
                      styles.feedbackButtonNeutral,
                    ]}
                    onPress={() => {
                      handleFeedback(
                        true,
                        'higher',
                        'More important than suggested'
                      );
                      setShowDetails(false);
                    }}
                  >
                    <Ionicons name='arrow-up' size={16} color='#FF9800' />
                    <Text style={styles.feedbackButtonText}>
                      Higher Priority
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[
                      styles.feedbackButton,
                      styles.feedbackButtonNegative,
                    ]}
                    onPress={() => {
                      handleFeedback(
                        false,
                        'lower',
                        'Not relevant to my goals'
                      );
                      setShowDetails(false);
                    }}
                  >
                    <Ionicons name='thumbs-down' size={16} color='#F44336' />
                    <Text style={styles.feedbackButtonText}>Not Helpful</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </>
  );
};

const LearningPathStepItem: React.FC<{
  step: LearningPathStep;
  index: number;
}> = ({ step, index }) => (
  <View style={styles.learningPathStep}>
    <View style={styles.stepNumber}>
      <Text style={styles.stepNumberText}>{index + 1}</Text>
    </View>
    <View style={styles.stepContent}>
      <Text style={styles.stepTitle}>{step.title}</Text>
      <View style={styles.stepMeta}>
        <Text style={styles.stepType}>{step.type}</Text>
        <Text style={styles.stepDuration}>
          {step.estimatedMinutes}m • Level {step.difficultyLevel}
        </Text>
      </View>
      {step.learningObjectives.length > 0 && (
        <Text style={styles.stepObjectives} numberOfLines={2}>
          Learn: {step.learningObjectives.join(', ')}
        </Text>
      )}
    </View>
  </View>
);

const ProgressIndicatorItem: React.FC<{ indicator: ProgressIndicator }> = ({
  indicator,
}) => {
  const progressPercentage =
    (indicator.currentValue / indicator.targetValue) * 100;

  return (
    <View style={styles.progressIndicatorItem}>
      <View style={styles.progressIndicatorHeader}>
        <Text style={styles.progressIndicatorMetric}>{indicator.metric}</Text>
        <Text style={styles.progressIndicatorValue}>
          {indicator.currentValue} / {indicator.targetValue} {indicator.unit}
        </Text>
      </View>
      <View style={styles.progressIndicatorBar}>
        <View
          style={[
            styles.progressIndicatorFill,
            { width: `${Math.min(progressPercentage, 100)}%` },
          ]}
        />
      </View>
    </View>
  );
};

export const SkillGapRecommendations: React.FC<
  SkillGapRecommendationsProps
> = ({
  recommendations,
  onRecommendationSelect,
  onFeedback,
  maxRecommendations = 5,
  showLearningPath = true,
}) => {
  const displayRecommendations = recommendations
    .sort((a, b) => {
      // Sort by priority (high > medium > low) then by gap size
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      const priorityDiff =
        priorityOrder[b.priority] - priorityOrder[a.priority];
      if (priorityDiff !== 0) return priorityDiff;
      return b.gapSize - a.gapSize;
    })
    .slice(0, maxRecommendations);

  const renderRecommendation = ({ item }: { item: SkillGapRecommendation }) => (
    <SkillGapCard
      recommendation={item}
      onPress={() => onRecommendationSelect(item)}
      onFeedback={feedback => onFeedback(item.id, feedback)}
      showLearningPath={showLearningPath}
    />
  );

  if (displayRecommendations.length === 0) {
    return (
      <View style={styles.emptyState}>
        <Ionicons name='school-outline' size={48} color='#ccc' />
        <Text style={styles.emptyStateText}>No skill gaps identified</Text>
        <Text style={styles.emptyStateSubtext}>
          Great job! Keep learning to discover new areas for growth
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Skill Gap Recommendations</Text>
        <Text style={styles.subtitle}>
          Areas where focused learning can accelerate your progress
        </Text>
      </View>

      <FlatList
        data={displayRecommendations}
        renderItem={renderRecommendation}
        keyExtractor={item => item.id}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.list}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: '#666',
  },
  list: {
    paddingHorizontal: 16,
  },
  skillGapCard: {
    marginBottom: 16,
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  skillGapGradient: {
    padding: 16,
  },
  skillGapHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  skillGapTitleContainer: {
    flex: 1,
    marginRight: 12,
  },
  skillGapTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#fff',
    marginBottom: 2,
  },
  skillGapCategory: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  detailsButton: {
    padding: 4,
  },
  gapAnalysis: {
    marginBottom: 12,
  },
  masteryProgress: {
    marginBottom: 8,
  },
  masteryLabel: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
    marginBottom: 4,
  },
  masteryBar: {
    height: 6,
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    borderRadius: 3,
    overflow: 'hidden',
  },
  masteryFill: {
    height: '100%',
    backgroundColor: '#fff',
    borderRadius: 3,
  },
  masteryValue: {
    fontSize: 12,
    color: '#fff',
    fontWeight: '600',
    marginTop: 4,
  },
  gapIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 4,
  },
  gapSize: {
    fontSize: 14,
    color: '#fff',
    fontWeight: '600',
    marginLeft: 4,
  },
  whyImportant: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.9)',
    marginBottom: 12,
    lineHeight: 20,
  },
  skillGapFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  timeEstimate: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  timeText: {
    fontSize: 12,
    color: '#fff',
    marginLeft: 4,
    fontWeight: '600',
  },
  priorityBadge: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  priorityText: {
    fontSize: 10,
    color: '#fff',
    fontWeight: 'bold',
  },
  learningPathPreview: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255, 255, 255, 0.2)',
  },
  learningPathTitle: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
    marginBottom: 4,
  },
  learningPathSteps: {
    fontSize: 12,
    color: '#fff',
    fontWeight: '500',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  emptyStateText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#666',
    marginTop: 16,
    textAlign: 'center',
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: '#999',
    marginTop: 8,
    textAlign: 'center',
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '90%',
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 40,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    flex: 1,
  },
  modalCloseButton: {
    padding: 4,
  },
  skillOverview: {
    marginBottom: 20,
  },
  skillStats: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 16,
    paddingVertical: 16,
    backgroundColor: '#f8f9fa',
    borderRadius: 12,
  },
  skillStatItem: {
    alignItems: 'center',
  },
  skillStatLabel: {
    fontSize: 12,
    color: '#666',
    marginBottom: 4,
  },
  skillStatValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  detailedMasteryBar: {
    marginBottom: 16,
  },
  masteryBarContainer: {
    height: 12,
    backgroundColor: '#e0e0e0',
    borderRadius: 6,
    position: 'relative',
    overflow: 'hidden',
  },
  currentMasteryFill: {
    height: '100%',
    backgroundColor: '#4CAF50',
    borderRadius: 6,
  },
  targetMasteryIndicator: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 2,
    backgroundColor: '#FF9800',
  },
  masteryLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  masteryLabelText: {
    fontSize: 12,
    color: '#666',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 12,
    marginTop: 20,
  },
  prerequisitesSection: {
    marginBottom: 20,
  },
  prerequisiteItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  prerequisiteText: {
    fontSize: 14,
    color: '#333',
    marginLeft: 8,
    flex: 1,
  },
  learningPathSection: {
    marginBottom: 20,
  },
  learningPathStep: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  stepNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#2196F3',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    marginTop: 2,
  },
  stepNumberText: {
    fontSize: 12,
    color: '#fff',
    fontWeight: 'bold',
  },
  stepContent: {
    flex: 1,
  },
  stepTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  stepMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  stepType: {
    fontSize: 12,
    color: '#666',
    textTransform: 'capitalize',
  },
  stepDuration: {
    fontSize: 12,
    color: '#666',
  },
  stepObjectives: {
    fontSize: 12,
    color: '#666',
    fontStyle: 'italic',
  },
  progressSection: {
    marginBottom: 20,
  },
  progressIndicatorItem: {
    marginBottom: 12,
  },
  progressIndicatorHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  progressIndicatorMetric: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
  },
  progressIndicatorValue: {
    fontSize: 12,
    color: '#666',
  },
  progressIndicatorBar: {
    height: 6,
    backgroundColor: '#e0e0e0',
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressIndicatorFill: {
    height: '100%',
    backgroundColor: '#4CAF50',
    borderRadius: 3,
  },
  feedbackSection: {
    marginTop: 20,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  feedbackTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 16,
  },
  feedbackButtons: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    flexWrap: 'wrap',
  },
  feedbackButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    marginBottom: 8,
  },
  feedbackButtonPositive: {
    borderColor: '#4CAF50',
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
  },
  feedbackButtonNeutral: {
    borderColor: '#FF9800',
    backgroundColor: 'rgba(255, 152, 0, 0.1)',
  },
  feedbackButtonNegative: {
    borderColor: '#F44336',
    backgroundColor: 'rgba(244, 67, 54, 0.1)',
  },
  feedbackButtonText: {
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 4,
    color: '#333',
  },
});

export default SkillGapRecommendations;
